import React, { useState } from 'react';

const FileUploadControls = ({ onControlsDataChange }) => {
  const [controls_data, setControlsData] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [fileName, setFileName] = useState('');

  // Handle drag over event
  const handleDragOver = (e) => {
    e.preventDefault();
    setDragging(true);
  };

  // Handle drag leave event
  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragging(false);
  };

  // Handle file drop event
  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    handleFile(file);
  };

  // Handle file input change event
  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    handleFile(file);
  };

  // Handle file upload logic
  const handleFile = (file) => {
    const fileType = file.type;

    if (fileType === 'text/csv') {
      setFileName(file.name); // Display file name
      parseCSV(file);
    } else {
      alert('File is not supported. Make sure to upload CSV.');
    }
  };

  // CSV Parsing function (handling quotes and commas)
  const parseCSV = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target.result;
        console.log('CSV content:', content.substring(0, 200) + '...'); // Log first 200 chars
        
        const rows = content.split('\n');
        console.log(`Found ${rows.length} rows in CSV`);
        
        if (rows.length < 2) {
          console.error('CSV has too few rows, needs at least header and one data row');
          alert('CSV file is invalid or empty. Please check the file format.');
          return;
        }

        // Parse header row
        const header = parseCSVRow(rows[0]);
        console.log('CSV headers:', header);
        
        // Check for required columns
        const requiredColumns = ['CONTROL_ID', 'CONTROL_TITLE', 'CONTROL_DESCRIPTION'];
        const missingColumns = requiredColumns.filter(col => !header.includes(col));
        
        if (missingColumns.length > 0) {
          console.error('Missing required columns:', missingColumns);
          alert(`CSV is missing required columns: ${missingColumns.join(', ')}`);
          return;
        }

        // Parse the rest of the rows
        const data = rows.slice(1)
          .filter(row => row.trim() !== '') // Skip empty rows
          .map((row, index) => {
            try {
              const values = parseCSVRow(row);
              const rowData = {};
              header.forEach((col, index) => {
                rowData[col.trim()] = values[index]?.trim() || ''; // Handling missing values
              });
              return rowData;
            } catch (err) {
              console.error(`Error parsing row ${index + 2}:`, row, err);
              return null;
            }
          })
          .filter(row => row !== null); // Remove rows that failed to parse
          
        console.log(`Successfully parsed ${data.length} data rows`);
        console.log('Sample parsed data:', data.slice(0, 2));

        if (data.length === 0) {
          alert('No valid data rows found in CSV. Please check the file format.');
          return;
        }
        
        // Check for duplicate CONTROL_IDs within the CSV file itself
        const controlIds = data.map(row => row.CONTROL_ID);
        const uniqueControlIds = new Set(controlIds);
        
        if (controlIds.length !== uniqueControlIds.size) {
          // Find the duplicates
          const duplicateIds = controlIds.filter((id, index) => controlIds.indexOf(id) !== index);
          const uniqueDuplicates = [...new Set(duplicateIds)];
          
          alert(`Duplicate CONTROL_IDs found in the CSV: ${uniqueDuplicates.join(', ')}. Please remove duplicates before uploading.`);
          return;
        }
        
        setControlsData(data);
        onControlsDataChange(data); // Pass controls data to parent
      } catch (error) {
        console.error('Error parsing CSV file:', error);
        alert('Error parsing CSV file: ' + error.message);
      }
    };
    
    reader.onerror = (error) => {
      console.error('Error reading file:', error);
      alert('Error reading file: ' + error);
    };
    
    reader.readAsText(file);
  };

  // Helper function to parse a CSV row
  const parseCSVRow = (row) => {
    const values = [];
    let inQuotes = false;
    let currentValue = '';
    
    for (let i = 0; i < row.length; i++) {
      const char = row[i];

      if (char === '"' && (i === 0 || row[i - 1] !== '"')) {
        // Toggle inQuotes when encountering a single quote
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        // If not in quotes, treat comma as separator
        values.push(currentValue);
        currentValue = '';
      } else {
        // Add char to the current value
        currentValue += char;
      }
    }
    values.push(currentValue); // Push the last value
    return values;
  };

  return (
    <div className="file-upload" style={{ textAlign: 'center', padding: '20px' }}>
      {/* Drop Area */}
      <div
        style={{
          border: dragging ? '2px solid #4CAF50' : '2px dashed #B0B0B0',
          borderRadius: '8px',
          padding: '40px 20px',
          textAlign: 'center',
          cursor: 'pointer',
          backgroundColor: dragging ? '#eaf7ea' : '#f9f9f9',
          marginBottom: '20px',
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        
        <input
          type="file"
          accept=".csv"
          onChange={handleFileInputChange}
          style={{ display: 'none' }}
          id="file-input"
        />
        <label htmlFor="file-input" style={{ cursor: 'pointer', color: 'grey', fontSize: '16px' }}>
          <p>Drag & drop the upload template here, or click here to browse the file.</p>
        </label>
      </div>

      {/* File name display outside the box */}
      {fileName && (
        <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#333' }}>
          <strong>File Name:</strong> {fileName}
        </div>
      )}

      {/* Table displaying the parsed CSV data */}
      {controls_data.length > 0 && (
        <div
          style={{
            marginTop: '20px',
            overflowX: 'auto', // Ensure horizontal scrolling if table is wide
          }}
        >
          <h6>Row Count: {controls_data.length}</h6>
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              margin: '0 auto',
              border: '1px solid #ddd',
              tableLayout: 'auto', // Ensure table layout adapts to modal
              maxWidth: '100%', // Prevent it from overflowing modal width
            }}
          >
            <thead>
              <tr>
                {Object.keys(controls_data[0]).map((header, index) => (
                  <th
                    key={index}
                    style={{
                      padding: '10px',
                      border: '1px solid #ddd',
                      backgroundColor: '#f2f2f2',
                      wordWrap: 'break-word', // Prevent header text overflow
                    }}
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {controls_data.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {Object.values(row).map((value, index) => (
                    <td
                      key={index}
                      style={{
                        fontSize: '12px',
                        padding: '8px',
                        border: '1px solid #ddd',
                        textAlign: 'center',
                        wordWrap: 'break-word', // Prevent content overflow in cells
                      }}
                    >
                      {value}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
         
    </div>

  );
};

export default FileUploadControls;
